import { supabase } from '../supabaseClient';
import { sha256ExternalText } from './externalPlanWorkflow';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SHA256_RE = /^[0-9a-f]{64}$/;

function requiredUuid(value, label) {
  const normalized = String(value || '').trim();
  if (!UUID_RE.test(normalized)) throw new Error(`${label} invalide.`);
  return normalized;
}

function requiredHash(value, label) {
  const normalized = String(value || '').trim().toLowerCase();
  if (!SHA256_RE.test(normalized)) throw new Error(`${label} invalide.`);
  return normalized;
}

function normalizedText(value) {
  const text = String(value || '').replace(/\r\n/g, '\n').trim();
  if (text.length < 80 || text.length > 500000) {
    throw new Error('La synthèse relue est vide ou trop courte.');
  }
  return text;
}

function unwrapRpcRow(data) {
  return Array.isArray(data) ? data[0] : data;
}

// Mémoire de l'onglet uniquement : aucune persistance localStorage/sessionStorage.
const ephemeralSynthesisText = new Map();

export function clearEphemeralExternalDossierSyntheses() {
  ephemeralSynthesisText.clear();
}

export async function saveExternalDossierSynthesis({
  clientId,
  synthesisText,
  synthesisSha256,
  profileSummarySha256,
  requestId,
  reviewedAt,
}, db = supabase) {
  const client = requiredUuid(clientId, 'Cliente');
  const request = requiredUuid(requestId, 'Identifiant de sauvegarde');
  const text = normalizedText(synthesisText);
  const textHash = requiredHash(synthesisSha256, 'Empreinte de synthèse');
  const profileHash = requiredHash(profileSummarySha256, 'Empreinte du profil');
  if (await sha256ExternalText(text) !== textHash) {
    throw new Error('La synthèse relue ne correspond pas à son empreinte.');
  }
  const reviewed = new Date(reviewedAt || '').toISOString();
  const { data, error } = await db.rpc('sauvegarder_synthese_dossier_externe', {
    p_client_id: client,
    p_profile_summary_sha256: profileHash,
    p_request_id: request,
    p_reviewed_at: reviewed,
    p_synthesis_sha256: textHash,
    p_synthesis_text: text,
  });
  if (error || !data) throw new Error(error?.message || 'La synthèse n’a pas été confirmée par le serveur.');
  const row = unwrapRpcRow(data);
  const receipt = {
    synthesis_id: requiredUuid(row?.synthesisId, 'Preuve de synthèse'),
    synthesis_sha256: requiredHash(row?.synthesisSha256, 'Empreinte de synthèse confirmée'),
    profile_summary_sha256: requiredHash(row?.profileSummarySha256, 'Empreinte du profil confirmée'),
  };
  if (receipt.synthesis_sha256 !== textHash || receipt.profile_summary_sha256 !== profileHash) {
    throw new Error('La confirmation serveur ne correspond pas à la synthèse relue.');
  }
  ephemeralSynthesisText.set(receipt.synthesis_id, text);
  return receipt;
}

export async function readExternalDossierSynthesisForGeneration({
  clientId,
  synthesisId,
  expectedSynthesisSha256,
}, db = supabase) {
  const expectedHash = requiredHash(expectedSynthesisSha256, 'Empreinte de synthèse attendue');
  const { data, error } = await db.rpc('verifier_synthese_dossier_externe_pour_generation', {
    p_client_id: requiredUuid(clientId, 'Cliente'),
    p_expected_synthesis_sha256: expectedHash,
    p_synthesis_id: requiredUuid(synthesisId, 'Preuve de synthèse'),
  });
  if (error || !data) throw new Error(error?.message || 'La preuve serveur de la synthèse est inaccessible.');
  const row = unwrapRpcRow(data);
  const confirmedId = requiredUuid(row?.synthesisId, 'Preuve de synthèse');
  const confirmedHash = requiredHash(row?.synthesisSha256, 'Empreinte de synthèse');
  const confirmedProfileHash = requiredHash(row?.profileSummarySha256, 'Empreinte du profil');
  if (confirmedId !== synthesisId || confirmedHash !== expectedHash) {
    throw new Error('La synthèse serveur ne correspond pas à la preuve du parcours.');
  }
  const text = ephemeralSynthesisText.get(synthesisId);
  if (!text) {
    throw new Error('Recoller la synthèse : son texte n’est plus disponible dans cet onglet.');
  }
  const actualHash = await sha256ExternalText(text);
  if (actualHash !== expectedHash) {
    throw new Error('La synthèse serveur ne correspond pas à la preuve du parcours.');
  }
  return {
    synthesisId,
    synthesisSha256: expectedHash,
    profileSummarySha256: confirmedProfileHash,
    text,
  };
}
